import { useId } from "react";
import type { Command } from "@shared-core/domain/object-ui/contract";
import { rowCommandsOf } from "@shared-core/domain/object-ui/row-commands";
import { Button } from "../primitives/button";
import { cn } from "../primitives/utils";
import { MoreCommands } from "./MoreCommands";

/**
 * A child row's commands (`rowCommandsOf`): up to two, neither destructive, as buttons; otherwise the row's main
 * command as its one button (the declared main while it can run, destructive or not, else the first that can run
 * and is not destructive)
 * and the rest behind More…, those that cannot run listed there under "Not available now" with their reason. A button
 * that cannot run says why in muted text under it, on a phone and on a desktop alike (a command that applies but cannot
 * run now is shown with its reason, never as a bare greyed button); the button points at it with `aria-describedby`.
 *
 * A click here never reaches the row (Enter and Space on a button are clicks too): a row that opens its record on a
 * click must not open it when a command is chosen. React passes a portal's events up the component tree, so this holds
 * for the More… menu's items too.
 */
export function RowCommands({
  commands,
  declaredMain = null,
  isPhone,
  onRun,
}: {
  commands: readonly Command[];
  /** The command the row's object declares as its main one (`declaredMainOf`), if any. */
  declaredMain?: string | null;
  isPhone: boolean;
  onRun: (command: Command) => void;
}) {
  const { buttons, more, unavailable } = rowCommandsOf(commands, declaredMain);
  return (
    <span
      className={cn(
        "flex gap-1.5",
        isPhone ? "flex-wrap items-start gap-2" : "items-center justify-end",
      )}
      onClick={(e) => e.stopPropagation()}
    >
      {buttons.map((command) => (
        <RowCommandButton
          key={command.name}
          command={command}
          isPhone={isPhone}
          onRun={onRun}
        />
      ))}
      {more.length + unavailable.length > 0 && (
        <MoreCommands
          commands={more}
          unavailable={unavailable}
          align={isPhone ? "start" : "end"}
          onRun={onRun}
        />
      )}
    </span>
  );
}

function RowCommandButton({
  command,
  isPhone,
  onRun,
}: {
  command: Command;
  isPhone: boolean;
  onRun: (command: Command) => void;
}) {
  const reasonId = useId();
  const reason = !command.enabled ? command.reason : null;
  return (
    <span
      className={cn(
        "flex flex-col gap-0.5",
        isPhone ? "max-w-56" : "max-w-64 items-end",
      )}
    >
      <Button
        size="sm"
        variant="outline"
        disabled={!command.enabled}
        aria-describedby={reason ? reasonId : undefined}
        onClick={() => onRun(command)}
        className={cn(
          "min-w-0 max-w-full",
          isPhone ? "h-10" : "whitespace-nowrap",
        )}
      >
        <span className="truncate">{command.label}</span>
      </Button>
      {reason && (
        <span
          id={reasonId}
          className={cn(
            "text-xs text-amber-700 dark:text-amber-400",
            !isPhone && "text-right",
          )}
        >
          {reason}
        </span>
      )}
    </span>
  );
}
